import { MonitorSeriesNames } from '../../../location-card.constants';
import { alarmToggleActions, enableField, disableField } from '../../actions';
import { Section } from '../../models';
import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { ForeSiteXDalarmsFieldIds, ForesiteXDSectionIds } from './base-config-models';
import { field } from '../../core/field-schema';
import { required, maxLength, pattern } from '../../core/validator-builders';
import { ForesiteXDLocationCardState } from './models';
import { roundToDecimal } from '../../models/utils';

export const getValidators = function (isMetric: boolean) {
    const validators = [];

    if (isMetric) {
        return [
            ...validators,
            pattern({
                value: REGEX_CONFIG.negativeWithOneDecimalPattern,
                message: 'COMMON.NUMERIC_PRECISION_ERROR_METRIC',
            }),
        ];
    } else {
        return [
            ...validators,
            pattern({
                value: REGEX_CONFIG.negativeWithTwoDecimalPattern,
                message: 'COMMON.NUMERIC_PRECISION_ERROR',
            }),
        ];
    }
};

/**
 * ForeSITE Alarms Configuration Section
 */
export const configureAlarms = (state: ForesiteXDLocationCardState): Section => {
    const config = state.monitorConfiguration;
    const settings = state.settings;

    const foresiteXdSeries = state.allMonitorSeries.find(
        (v) => v.value.toLowerCase() === MonitorSeriesNames.ForeSITE_XD.toLowerCase(),
    );

    const depthAlarmValidators = [required(), ...getValidators(settings.isMetric)];

    return {
        info: {
            id: ForesiteXDSectionIds.ConfigureAlarms,
            title: 'ADD_EDIT.ADD_EDIT_CONFIGURE',
            displayTitle: true,
            expandable: true,
            expanded: false,
        },
        rows: [
            // Low Level Alarm Row
            {
                info: {
                    id: 'low-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ForeSiteXDalarmsFieldIds.LowLevelEnable, 'lowLevelEnable', {
                        placeholder: 'COMMON.LOW_LEVEL_ALARM',
                        value: config?.lowlevelenable ?? false,
                        panelClass: 'alarm-slide-toggle',
                        events: {
                            onChange: (value) => alarmToggleActions(value, ForeSiteXDalarmsFieldIds.LowLevelThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(ForeSiteXDalarmsFieldIds.LowLevelThreshold, 'lowLevelThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_LOW_LEVEL',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: roundToDecimal(config?.lowlevelthreshold, 1),
                        decimalPlaces: settings.isMetric ? 1 : 2,
                        disabled: config && config.lowlevelenable ? false : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },

            // High Level Alarm Row
            {
                info: {
                    id: 'high-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ForeSiteXDalarmsFieldIds.HighLevelEnable, 'highLevelEnable', {
                        placeholder: 'COMMON.HIGH_LEVEL_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: config?.highlevelenable ?? false,
                        events: {
                            onChange: (value) => alarmToggleActions(value, ForeSiteXDalarmsFieldIds.HighLevelThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(ForeSiteXDalarmsFieldIds.HighLevelThreshold, 'highLevelThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_HIGH_LEVEL',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: roundToDecimal(config?.highlevelthreshold, 1),
                        decimalPlaces: settings.isMetric ? 1 : 2,
                        disabled: config && config.highlevelenable ? false : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },

            // High-High Level Alarm Row
            {
                info: {
                    id: 'high-high-level-alarms-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ForeSiteXDalarmsFieldIds.HighHighEnable, 'highHighEnable', {
                        placeholder: 'COMMON.HIGH_HIGH_ALARAM',
                        panelClass: 'alarm-slide-toggle',
                        value: config?.highhighenable ?? false,
                        events: {
                            onChange: (value) => alarmToggleActions(value, ForeSiteXDalarmsFieldIds.HighHighThreshold, depthAlarmValidators),
                        },
                    }),
                    field.number(ForeSiteXDalarmsFieldIds.HighHighThreshold, 'highHighThreshold', {
                        placeholder: 'COMMON.DEPTH_THRESHOLD_OF_HIGH_HIGH',
                        placeholderSuffix: settings.depthUnit,
                        panelClass: 'alarm-input',
                        value: roundToDecimal(config?.highhighthreshold, settings.isMetric ? 1 : 2),
                        decimalPlaces: settings.isMetric ? 1 : 2,
                        disabled: config && config.highhighenable ? false : true,
                        validators: depthAlarmValidators,
                    }),
                ],
            },

            // Battery Low Alarm Row
            {
                info: {
                    id: 'battery-alarm-row',
                    panelClass: 'each-configure-alarm',
                },
                items: [
                    field.toggle(ForeSiteXDalarmsFieldIds.BatteryLowEnable, 'batteryLowEnable', {
                        placeholder: 'COMMON.BATTERY_LOW_ALARM',
                        panelClass: 'alarm-slide-toggle',
                        value: config?.batterylowenable ?? false,
                        events: {
                            onChange: (value) => [
                                value
                                    ? enableField({ fieldId: ForeSiteXDalarmsFieldIds.BatteryLowThreshold })
                                    : disableField({ fieldId: ForeSiteXDalarmsFieldIds.BatteryLowThreshold }),
                            ],
                        },
                    }),
                    field.number(ForeSiteXDalarmsFieldIds.BatteryLowThreshold, 'batteryLowThreshold', {
                        placeholder: 'COMMON.BATTERY_THRESHOLD_INPUT_PLACEHOLDER',
                        placeholderSuffix: settings.voltUnit,
                        panelClass: 'alarm-input',
                        value: config?.batterylowthreshold ?? foresiteXdSeries.batteryLowInt,
                        disabled: config && config.batterylowenable ? false : true,
                        validators: [
                            required(),
                            maxLength({ value: 100 }),
                            pattern({
                                value: settings.isMetric
                                    ? REGEX_CONFIG.installationMetricValuePattern
                                    : REGEX_CONFIG.installationValuePattern,
                                message: 'COMMON.NUMERIC_PRECISION_ERROR',
                            }),
                        ],
                    }),
                ],
            },
        ],
    };
};
